import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { invoiceApi } from "../services/invoiceApi";
import { settingsApi } from "../services/settingsApi";

const money = (value) => Number(Number(value || 0).toFixed(2));

const emptyItem = () => ({
  costPriceId: "",
  description: "",
  quantity: 1,
  unitPrice: 0,
});

const paymentModes = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
  { value: "cheque", label: "Cheque" },
  { value: "online", label: "Online" },
  { value: "other", label: "Other" },
];

const statusOptions = [
  { value: "draft", label: "Draft" },
  { value: "pending", label: "Pending" },
  { value: "partial", label: "Partial" },
  { value: "paid", label: "Paid" },
  { value: "overdue", label: "Overdue" },
];

const currencyOptions = ["USD", "GBP", "PKR", "EUR", "AED", "SAR"];

function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-3xl border border-slate-200 bg-white shadow-sm transition-colors duration-300 dark:border-slate-800 dark:bg-slate-900 ${className}`}
    >
      {children}
    </div>
  );
}

function Toggle({ enabled, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      className={`relative h-7 w-14 rounded-full transition ${
        enabled ? "bg-blue-600 dark:bg-blue-500" : "bg-slate-300 dark:bg-slate-700"
      }`}
      aria-pressed={enabled}
    >
      <span
        className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition ${
          enabled ? "left-7" : "left-0.5"
        }`}
      />
    </button>
  );
}

function MoneyBlock({ label, value, currency }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
      <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
      <p className="mt-2 text-xl font-semibold text-slate-900 dark:text-slate-100">
        {currency} {Number(value || 0).toFixed(2)}
      </p>
    </div>
  );
}

export default function InvoiceCreate() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [user, setUser] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [costPrices, setCostPrices] = useState([]);

  const [savedSettings, setSavedSettings] = useState({
    companyName: "",
    companyAddress: "",
    logoDataUrl: "",
    invoiceShowLogo: true,
    invoiceShowTitle: true,
    invoiceTitle: "INVOICE",
    invoiceCurrency: "USD",
    invoiceTaxEnabled: false,
    invoiceTaxRate: 0,
    invoiceDiscountEnabled: false,
    invoiceDiscountRate: 0,
  });

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [fromName, setFromName] = useState("");
  const [fromAddress, setFromAddress] = useState("");
  const [toName, setToName] = useState("");
  const [toEmail, setToEmail] = useState("");
  const [toAddress, setToAddress] = useState("");
  const [showFromAddress, setShowFromAddress] = useState(false);
  const [showToAddress, setShowToAddress] = useState(false);

  const [invoiceDate, setInvoiceDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [dueDate, setDueDate] = useState("");
  const [subject, setSubject] = useState("");
  const [paymentMode, setPaymentMode] = useState("bank_transfer");
  const [status, setStatus] = useState("draft");

  const [currency, setCurrency] = useState("USD");

  const [showLogo, setShowLogo] = useState(true);
  const [showTitle, setShowTitle] = useState(true);
  const [taxEnabled, setTaxEnabled] = useState(false);
  const [taxRate, setTaxRate] = useState(0);
  const [secondaryTaxEnabled, setSecondaryTaxEnabled] = useState(false);
  const [secondaryTaxRate, setSecondaryTaxRate] = useState(0);
  const [discountEnabled, setDiscountEnabled] = useState(false);
  const [discountRate, setDiscountRate] = useState(0);

  const [paidAmount, setPaidAmount] = useState(0);
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([emptyItem()]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [meRes, settingsRes, vendorsRes, costPricesRes] =
          await Promise.allSettled([
            api.get("/auth/me"),
            settingsApi.getMy(),
            api.get("/vendors"),
            api.get("/cost-prices"),
          ]);

        const freshUser =
          meRes.status === "fulfilled" ? meRes.value.data?.user || null : null;

        if (freshUser) setUser(freshUser);

        const freshCompanyName = freshUser?.name || "";

        if (settingsRes.status === "fulfilled") {
          const s = settingsRes.value.data?.settings || {};
          setSavedSettings({
            companyName: s.companyName || "",
            companyAddress: s.companyAddress || "",
            logoDataUrl: s.logoDataUrl || "",
            invoiceShowLogo: Boolean(s.invoiceShowLogo),
            invoiceShowTitle: Boolean(s.invoiceShowTitle),
            invoiceTitle: s.invoiceTitle || "INVOICE",
            invoiceCurrency: s.invoiceCurrency || "USD",
            invoiceTaxEnabled: Boolean(s.invoiceTaxEnabled),
            invoiceTaxRate: Number(s.invoiceTaxRate || 0),
            invoiceDiscountEnabled: Boolean(s.invoiceDiscountEnabled),
            invoiceDiscountRate: Number(s.invoiceDiscountRate || 0),
          });

          setShowLogo(Boolean(s.invoiceShowLogo));
          setShowTitle(Boolean(s.invoiceShowTitle));
          setCurrency(s.invoiceCurrency || "USD");
          setTaxEnabled(Boolean(s.invoiceTaxEnabled));
          setTaxRate(Number(s.invoiceTaxRate || 0));
          setDiscountEnabled(Boolean(s.invoiceDiscountEnabled));
          setDiscountRate(Number(s.invoiceDiscountRate || 0));
          setFromName(s.companyName || freshCompanyName);
          setFromAddress(s.companyAddress || "");
        } else {
          setFromName(freshCompanyName);
        }

        if (vendorsRes.status === "fulfilled") {
          const data = vendorsRes.value.data;
          setVendors(Array.isArray(data?.vendors) ? data.vendors : data || []);
        }

        if (costPricesRes.status === "fulfilled") {
          const data = costPricesRes.value.data;
          setCostPrices(
            Array.isArray(data?.costPrices) ? data.costPrices : data || []
          );
        }
      } catch {
        // keep page usable even if lookups fail
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const combinedTaxRate = useMemo(() => {
    if (!taxEnabled) return 0;
    return money(
      Number(taxRate || 0) +
        (secondaryTaxEnabled ? Number(secondaryTaxRate || 0) : 0)
    );
  }, [taxEnabled, taxRate, secondaryTaxEnabled, secondaryTaxRate]);

  const totals = useMemo(() => {
    let subtotal = 0;

    items.forEach((item) => {
      const qty = Number(item.quantity || 0);
      const price = Number(item.unitPrice || 0);
      subtotal += qty * price;
    });

    subtotal = money(subtotal);
    const taxAmount = money(subtotal * (combinedTaxRate / 100));
    const discountAmount = money(
      discountEnabled ? subtotal * (Number(discountRate || 0) / 100) : 0
    );
    const totalAmount = money(subtotal + taxAmount - discountAmount);
    const balanceAmount = money(totalAmount - Number(paidAmount || 0));

    return {
      subtotal,
      taxAmount,
      discountAmount,
      totalAmount,
      balanceAmount,
    };
  }, [items, combinedTaxRate, discountEnabled, discountRate, paidAmount]);

  useEffect(() => {
    const loadInvoiceNumber = async () => {
      try {
        const res = await invoiceApi.getNextNumber();
        setInvoiceNumber(res.data.invoiceNumber);
      } catch {
        console.error("Failed to get invoice number");
      }
    };

    loadInvoiceNumber();
  }, []);

  const setItemField = (index, field, value) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;

        const updated = { ...item, [field]: value };

        if (field === "costPriceId") {
          const selected = costPrices.find(
            (cp) => String(cp.id) === String(value)
          );
          if (selected) {
            updated.description =
              selected.productName ||
              selected.name ||
              selected.title ||
              selected.description ||
              "";
            updated.unitPrice = Number(
              selected.price ??
                selected.unitPrice ??
                selected.costPrice ??
                selected.total ??
                0
            );
          }
        }

        return updated;
      })
    );
  };

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (index) =>
    setItems((prev) =>
      prev.length === 1 ? prev : prev.filter((_, i) => i !== index)
    );

  const handlePrint = () => {
    window.print();
  };

  const handleSend = () => {
    if (!toEmail.trim()) {
      setError("Add recipient email to send the invoice.");
      return;
    }

    const subjectText = encodeURIComponent(
      subject?.trim() ? subject.trim() : `Invoice ${invoiceNumber}`
    );
    const body = encodeURIComponent(
      `Hello ${toName || "Client"},\n\nPlease find your invoice below:\n\nInvoice #: ${invoiceNumber}\nAmount Due: ${currency} ${totals.totalAmount.toFixed(
        2
      )}\n\nThanks.`
    );

    window.location.href = `mailto:${toEmail.trim()}?subject=${subjectText}&body=${body}`;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      const cleanItems = items
        .map((item) => {
          const quantity = Number(item.quantity || 0);
          const unitPrice = Number(item.unitPrice || 0);
          const lineBase = money(quantity * unitPrice);

          return {
            costPriceId: item.costPriceId ? Number(item.costPriceId) : null,
            description: (item.description || "").trim(),
            quantity,
            unitPrice,
            taxRate: combinedTaxRate,
            taxAmount: money(lineBase * (combinedTaxRate / 100)),
          };
        })
        .filter((item) => item.description && item.quantity > 0);

      if (!invoiceNumber.trim()) throw new Error("Invoice number is required");
      if (!invoiceDate) throw new Error("Invoice date is required");
      if (!toName.trim()) throw new Error("Client/recipient name is required");
      if (cleanItems.length === 0) throw new Error("Add at least one invoice item");

      const payload = {
        invoiceNumber: invoiceNumber.trim(),
        vendorId: null,
        supplierName: toName.trim(),
        invoiceDate,
        dueDate: dueDate || null,
        paymentMode,
        status,
        currency,
        taxMode: taxEnabled ? "percent" : "amount",
        taxRate: taxEnabled ? combinedTaxRate : 0,
        taxAmount: totals.taxAmount,
        discountAmount: totals.discountAmount,
        paidAmount: Number(paidAmount || 0),
        notes: notes.trim() || null,
        items: cleanItems,
      };

      const res = await invoiceApi.create(payload);
      const createdId = res.data?.invoice?.id;

      if (createdId) {
        navigate(`/invoices/${createdId}/print`);
      } else {
        navigate("/invoices");
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to create invoice");
    } finally {
      setSaving(false);
    }
  };

  const showLogoPanel = showLogo;
  const topGridClass = showLogo
    ? "mt-8 grid gap-6 lg:grid-cols-[150px_1fr]"
    : "mt-8";

  const inputClass =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500";
  const labelClass = "mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300";
  const tableInputClass =
    "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100";
  const selectClass = inputClass;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 dark:bg-slate-950">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100">
          Loading invoice page...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 text-slate-900 dark:bg-slate-950 dark:text-slate-100 md:p-6">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
          .print-card { box-shadow: none !important; border: none !important; }
        }
      `}</style>

      <form onSubmit={handleCreate}>
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_330px]">
          <div className="space-y-5">
            <Card className="print-card p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  {showTitle ? (
                    <div className="flex flex-wrap items-center gap-3">
                      <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                        {savedSettings.invoiceTitle || "INVOICE"}
                      </h1>
                      <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                        DRAFT
                      </span>
                    </div>
                  ) : (
                    <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                      DRAFT
                    </span>
                  )}
                </div>

                <div className="text-right text-sm text-slate-500 dark:text-slate-400">
                  <div>{user?.name || fromName || "User"}</div>
                  <div>{user?.email || ""}</div>
                </div>
              </div>

              <div className={topGridClass}>
                {showLogo ? (
                  <div className="flex items-start">
                    <div className="flex h-[120px] w-[120px] shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-dashed border-orange-300 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/40">
                      {savedSettings.logoDataUrl ? (
                        <img
                          src={savedSettings.logoDataUrl}
                          alt="Logo"
                          className="h-full w-full object-contain"
                        />
                      ) : (
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          No Logo
                        </span>
                      )}
                    </div>
                  </div>
                ) : null}

                <div className="grid w-full gap-5 sm:grid-cols-2">
                  <div>
                    <label className={labelClass}>Invoice #</label>
                    <input
                      value={invoiceNumber || "Loading..."}
                      readOnly
                      className="w-full cursor-not-allowed rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                    />
                  </div>

                  <div>
                    <label className={labelClass}>From*</label>
                    <input
                      value={fromName}
                      onChange={(e) => setFromName(e.target.value)}
                      className={inputClass}
                      placeholder="Your name or company"
                    />
                    <button
                      type="button"
                      onClick={() => setShowFromAddress((v) => !v)}
                      className="mt-2 text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
                    >
                      Add address
                    </button>
                    {showFromAddress ? (
                      <textarea
                        value={fromAddress}
                        onChange={(e) => setFromAddress(e.target.value)}
                        rows="3"
                        className={`${inputClass} mt-2`}
                        placeholder="Your address"
                      />
                    ) : null}
                  </div>

                  <div>
                    <label className={labelClass}>Issued on</label>
                    <input
                      type="date"
                      value={invoiceDate}
                      onChange={(e) => setInvoiceDate(e.target.value)}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>To*</label>
                    <input
                      value={toName}
                      onChange={(e) => setToName(e.target.value)}
                      className={inputClass}
                      placeholder="Client or recipient name"
                    />
                    <button
                      type="button"
                      onClick={() => setShowToAddress((v) => !v)}
                      className="mt-2 text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
                    >
                      Add address
                    </button>
                    {showToAddress ? (
                      <div className="mt-2 space-y-2">
                        <input
                          value={toEmail}
                          onChange={(e) => setToEmail(e.target.value)}
                          className={inputClass}
                          placeholder="Recipient email"
                        />
                        <textarea
                          value={toAddress}
                          onChange={(e) => setToAddress(e.target.value)}
                          rows="3"
                          className={inputClass}
                          placeholder="Recipient address"
                        />
                      </div>
                    ) : null}
                  </div>

                  <div>
                    <label className={labelClass}>Due date</label>
                    <input
                      type="date"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Subject</label>
                    <input
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className={inputClass}
                      placeholder="Invoice subject"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-8 border-t border-slate-200 pt-6 dark:border-slate-800">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    Invoice Items
                  </h2>
                  <button
                    type="button"
                    onClick={addItem}
                    className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
                  >
                    + Add item
                  </button>
                </div>

                <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-800">
                  <table className="min-w-full">
                    <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-950 dark:text-slate-400">
                      <tr>
                        <th className="px-4 py-3">Cost Price</th>
                        <th className="px-4 py-3">Description</th>
                        <th className="px-4 py-3">Quantity</th>
                        <th className="px-4 py-3">Unit price</th>
                        <th className="px-4 py-3">Amount</th>
                        <th className="px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {items.map((item, index) => {
                        const lineAmount = money(
                          Number(item.quantity || 0) * Number(item.unitPrice || 0)
                        );

                        return (
                          <tr
                            key={index}
                            className="bg-white text-slate-700 dark:bg-slate-900 dark:text-slate-200"
                          >
                            <td className="px-4 py-3">
                              <select
                                value={item.costPriceId}
                                onChange={(e) =>
                                  setItemField(index, "costPriceId", e.target.value)
                                }
                                className={tableInputClass}
                              >
                                <option value="">Manual</option>
                                {costPrices.map((cp) => (
                                  <option key={cp.id} value={cp.id}>
                                    {cp.productName ||
                                      cp.name ||
                                      cp.title ||
                                      cp.description ||
                                      `Item ${cp.id}`}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-4 py-3">
                              <input
                                value={item.description}
                                onChange={(e) =>
                                  setItemField(index, "description", e.target.value)
                                }
                                className={tableInputClass}
                                placeholder="Task name"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.quantity}
                                onChange={(e) =>
                                  setItemField(index, "quantity", e.target.value)
                                }
                                className="w-24 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.unitPrice}
                                onChange={(e) =>
                                  setItemField(index, "unitPrice", e.target.value)
                                }
                                className="w-28 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                              />
                            </td>
                            <td className="px-4 py-3 font-medium text-slate-700 dark:text-slate-200">
                              {currency} {lineAmount.toFixed(2)}
                            </td>
                            <td className="px-4 py-3">
                              <button
                                type="button"
                                onClick={() => removeItem(index)}
                                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                              >
                                ×
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 grid gap-4 md:grid-cols-3">
                  <MoneyBlock label="Subtotal" value={totals.subtotal} currency={currency} />
                  <MoneyBlock label="Tax" value={totals.taxAmount} currency={currency} />
                  <MoneyBlock
                    label="Amount Due"
                    value={totals.totalAmount}
                    currency={currency}
                  />
                </div>

                {notes ? (
                  <div className="mt-6 rounded-2xl border border-slate-200 p-5 dark:border-slate-800">
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      Additional notes
                    </h3>
                    <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                      {notes}
                    </p>
                  </div>
                ) : null}
              </div>

              <div className="no-print mt-6 flex flex-col gap-3 border-t border-slate-200 pt-6 md:flex-row md:items-center md:justify-between dark:border-slate-800">
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleSend}
                    className="rounded-xl bg-emerald-600 px-5 py-3 text-sm font-medium text-white transition hover:bg-emerald-700"
                  >
                    Send invoice
                  </button>
                  <button
                    type="button"
                    onClick={handlePrint}
                    className="rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    Download PDF
                  </button>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => navigate("/invoices")}
                    className="rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {saving ? "Creating..." : "Create Invoice"}
                  </button>
                </div>
              </div>

              {error ? (
                <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
                  {error}
                </div>
              ) : null}
            </Card>
          </div>

          <div className="no-print space-y-5 xl:sticky xl:top-6 xl:self-start">
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Get paid via:
                </div>
                <div className="text-sm text-blue-600 dark:text-blue-400">
                  Learn more
                </div>
              </div>
              <div className="mt-3 space-y-2 text-sm text-slate-700 dark:text-slate-300">
                <div>✓ Credit card / Debit card</div>
                <div>✓ ACH</div>
                <div>✓ Wire transfer</div>
              </div>
              <div className="mt-4 border-t border-slate-100 pt-3 text-sm text-slate-500 dark:border-slate-800 dark:text-slate-400">
                Powered by your invoice settings
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-full bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300">
                  ⚙
                </div>
                <div className="font-semibold text-slate-900 dark:text-slate-100">
                  Customise invoice
                </div>
              </div>

              <div className="mt-4 border-t border-slate-100 pt-4 dark:border-slate-800">
                <div className="text-xs font-semibold uppercase tracking-wide text-orange-500">
                  Appearance
                </div>

                <div className="mt-4 space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-medium text-slate-800 dark:text-slate-200">
                        Add a logo
                      </div>
                    </div>
                    <Toggle enabled={showLogo} onChange={setShowLogo} />
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-medium text-slate-800 dark:text-slate-200">
                        Show invoice title
                      </div>
                    </div>
                    <Toggle enabled={showTitle} onChange={setShowTitle} />
                  </div>
                </div>

                <div className="mt-6 text-xs font-semibold uppercase tracking-wide text-orange-500">
                  Invoice settings
                </div>

                <div className="mt-4 space-y-4">
                  <div>
                    <label className={labelClass}>Currency</label>
                    <select
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      className={selectClass}
                    >
                      {currencyOptions.map((cur) => (
                        <option key={cur} value={cur}>
                          {cur}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="font-medium text-slate-800 dark:text-slate-200">
                          Tax
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          Add up to 2 tax rates
                        </p>
                      </div>
                      <Toggle enabled={taxEnabled} onChange={setTaxEnabled} />
                    </div>

                    {taxEnabled ? (
                      <div className="mt-3 space-y-3">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={taxRate}
                          onChange={(e) => setTaxRate(e.target.value)}
                          className={inputClass}
                          placeholder="0.00"
                        />
                        {secondaryTaxEnabled ? (
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={secondaryTaxRate}
                            onChange={(e) => setSecondaryTaxRate(e.target.value)}
                            className={inputClass}
                            placeholder="Second tax rate"
                          />
                        ) : null}
                        <button
                          type="button"
                          onClick={() => setSecondaryTaxEnabled((v) => !v)}
                          className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
                        >
                          {secondaryTaxEnabled ? "Remove another" : "Apply another"}
                        </button>
                      </div>
                    ) : null}
                  </div>

                  <div>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="font-medium text-slate-800 dark:text-slate-200">
                          Discount
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          Apply a discount percentage
                        </p>
                      </div>
                      <Toggle enabled={discountEnabled} onChange={setDiscountEnabled} />
                    </div>

                    {discountEnabled ? (
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={discountRate}
                        onChange={(e) => setDiscountRate(e.target.value)}
                        className={`mt-3 ${inputClass}`}
                        placeholder="0.00"
                      />
                    ) : null}
                  </div>

                  <div>
                    <label className={labelClass}>Payment mode</label>
                    <select
                      value={paymentMode}
                      onChange={(e) => setPaymentMode(e.target.value)}
                      className={selectClass}
                    >
                      {paymentModes.map((mode) => (
                        <option key={mode.value} value={mode.value}>
                          {mode.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Status</label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      className={selectClass}
                    >
                      {statusOptions.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Paid amount</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={paidAmount}
                      onChange={(e) => setPaidAmount(e.target.value)}
                      className={inputClass}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
}